import { Controller, useFormContext, useWatch } from 'react-hook-form';

import PlanningEntityPicker from 'components/shared/PlanningEntityPicker';

import { PARTICIPANT_ENTITY_TYPES } from '../../../../types';
import AppointmentsOverviewActivityFilter from '../../../AppointmentsOverviewActivityFilter';
import AppointmentsOverviewAppliedFilters from '../../../AppointmentsOverviewAppliedFilters';
import { FilterFieldsStack } from './AppointmentsOverviewFilterFields.styles';

import type { PlanningEntitySelection } from 'components/shared/PlanningEntityPicker';
import type { AppointmentsOverviewFiltersForm } from '../../AppointmentsOverviewFilters.types';

/** Wide enough for the picker's type chips — Alle to Planeenheden — to sit on one line in its list. */
const PICKER_MIN_WIDTH = 480;

/**
 * The body of the filter panel: who (the picker) and which appointments (the activity), and below them
 * the draft as chips, each with ✕. Every change goes to the draft only; Toepassen in the panel's footer
 * applies it. The chips leave out their own Alles wissen, as the footer has Filters wissen.
 */
const AppointmentsOverviewFilterFields = () => {
  'use memo';

  const { control, setValue } =
    useFormContext<AppointmentsOverviewFiltersForm>();
  // useWatch rather than watch(): a watch() call in a 'use memo' component can be memoised into a stale
  // value.
  const selections = useWatch({ control, name: 'selections' });
  const activityIds = useWatch({ control, name: 'activityIds' });

  // A ✕ on a chip is a change to the draft like a pick, so it marks the panel as having unapplied changes.
  const handleSelectionsChange = (next: PlanningEntitySelection[]) =>
    setValue('selections', next, { shouldDirty: true });
  const handleActivityIdsChange = (next: string[]) =>
    setValue('activityIds', next, { shouldDirty: true });

  return (
    <FilterFieldsStack>
      <Controller
        control={control}
        name="selections"
        render={({ field }) => (
          <PlanningEntityPicker
            value={field.value}
            onChange={field.onChange}
            entityTypes={PARTICIPANT_ENTITY_TYPES}
            searchOrganizationalUnits
            showSelection={false}
            minWidth={PICKER_MIN_WIDTH}
          />
        )}
      />
      <Controller
        control={control}
        name="activityIds"
        render={({ field }) => (
          <AppointmentsOverviewActivityFilter
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <AppointmentsOverviewAppliedFilters
        selections={selections}
        onSelectionsChange={handleSelectionsChange}
        activityIds={activityIds}
        onActivityIdsChange={handleActivityIdsChange}
        showClearAll={false}
      />
    </FilterFieldsStack>
  );
};

export default AppointmentsOverviewFilterFields;
